import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../../app/useAolSaveHandler';
import { EngineDataTypeEnum, NoonReportSectionEnum, NoonReportTypeEnum } from '../../../../types/ViewModel/AolEnums';
import { AolDataItem } from '../../../../types/ViewModel/AolItem';
import { Vessel } from '../../../../types/ViewModel/Vessel/Vessel';
import {
    aolConstants,
    getConsumptionCost,
    getConsumptionCostByFuelType,
    hasInvalidFields,
    hasMissingValues,
} from '../../../../utils/aolFieldsHelper';
import { ErrorText } from '../../../shared/error-text/ErrorText.style';
import { SectionInnerWrapper, SubSectionHeader } from '../../aol-container/AolContainer.style';
import OdfjellInputComponent, { InputChangeData, TimeInputChangeData } from '../../../common/odfjell-input-component/OdfjellInputComponent';
import { EngineDataVM, EnginesDataVM } from '../EnginesSection';
import { ErrorMessageWrapper } from '../EnginesSection.style';
import { roundTo0Decimal } from '../../../../utils/numberHelper';
import { MainEngineSectionWrapper } from './MainEngineSection.style';
import { WarningText } from '../../../shared/warning-text/WarningText.style';
import useAolItemsHelper from '../../../../utils/aol/useAolItemsHelper';
import ConsumptionContainer from '../../consumption/ConsumptionContainer';
import { Consumption } from 'src/types/ViewModel/Aol/Consumption';
import { isNullOrUndefined } from 'src/utils/objectUtils';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';

type Props = {
    enginesDataVM: EnginesDataVM;
    onChange: () => void;
    vessel: Vessel | null;
    hoursInReport?: number;
    reportType: NoonReportTypeEnum;
    aolDataItem: AolDataItem;
    hasEmptyValues: any;
    isReadOnly: boolean;
    addConsumptionRow: (consumption: Consumption[]) => void;
};

const MainEngineSectionComponent: React.FC<Props> = ({
    enginesDataVM,
    onChange,
    vessel,
    hoursInReport,
    reportType,
    aolDataItem,
    hasEmptyValues,
    isReadOnly,
    addConsumptionRow,
}) => {
    const { saveAolEvent } = useAolSaveHandler();
    const [hasEmptyValuesLocal, setHasEmptyValuesLocal] = useState(false);
    const [isRunningHoursInconsistent, setIsRunningHoursInconsistent] = useState(false);
    const [slipDetailsExpanded, setSlipDetailsExpanded] = useState(false);
    const [sfocDetailsExpanded, setSfocDetailsExpanded] = useState(false);
    const [hasMissingMeConsumption, setHasMissingMeConsumption] = useState<boolean>(false);
    const { getVoyageFuelPrice } = useAolItemsHelper();

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.type === 'save_section' &&
            saveAolEvent.section === NoonReportSectionEnum.Engines &&
            aolDataItem &&
            saveAolEvent.aolDataItem.id === aolDataItem.id
        ) {
            checkOnSave();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (aolDataItem && vessel) {
            setHasEmptyValuesLocal(false);
            updateTotalMainEngineConsumption();
            updateSlipDetails();
            updateSfocDetails();
            updateHasMissingMeConsumption(enginesDataVM);
        }
    }, [aolDataItem, vessel]);

    const updateSlipDetails = () => {
        enginesDataVM.slipDetails = [];
        const mainEngineData = enginesDataVM?.mainEngineData;
        if (showSlip(mainEngineData, enginesDataVM.observedSpeed)) {
            if (!mainEngineData) return '';
            const mainEngineRpmReductionFactor = vessel?.mainEngineRpmReductionFactor;
            const hasMainEngineRpmReductionFactor = mainEngineRpmReductionFactor && mainEngineRpmReductionFactor !== 1;
            enginesDataVM.slipDetails.push('Theoretical Speed = Pitch / 10  * RPM * 60 / 1852');

            if (hasMainEngineRpmReductionFactor) {
                enginesDataVM.slipDetails.push(' * Reduction Factor');
            }
            const reductionFactorText = hasMainEngineRpmReductionFactor ? ` * ${mainEngineRpmReductionFactor}` : '';
            enginesDataVM.slipDetails.push(
                `Theoretical Speed = ${mainEngineData.pitch} / 10  * ${mainEngineData.revolutionsPerMinute} * 60 / 1852${reductionFactorText}`
            );

            const theoreticalSpeed = getTheoreticalSpeed(mainEngineData, enginesDataVM.observedSpeed);

            enginesDataVM.slipDetails.push(`Theoretical Speed = ${theoreticalSpeed.toFixed(2)} knot`);
            enginesDataVM.slipDetails.push(`Slip = 1 - Observed Speed / Theoretical Speed`);
            enginesDataVM.slipDetails.push(`Slip = 1 - ${enginesDataVM.observedSpeed} / ${theoreticalSpeed.toFixed(2)}`);
            enginesDataVM.slipDetails.push(`Slip = ${getSlip(mainEngineData, enginesDataVM.observedSpeed)?.toFixed(2)} %`);
        }
    };

    const updateSfocDetails = () => {
        enginesDataVM.sfocDetails = [];
        const mainEngineData = enginesDataVM?.mainEngineData;
        const shaftGeneratorData = enginesDataVM?.shaftGeneratorData;

        if (!mainEngineData || !shaftGeneratorData) return '';

        const hasShaftGenerator = vessel?.vesselConfigurationV3!.isEquippedWithShaftGenerator;
        const hasPowerLogging = vessel?.vesselConfigurationV3!.isEquippedWithPowerLoggingForAuxiliaryEngine;
        const consumptionHfo = mainEngineData.consumptionHfo ? mainEngineData.consumptionHfo : 0;
        const consumptionMgo = mainEngineData.consumptionMgo ? mainEngineData.consumptionMgo : 0;
        let consumption = 0;
        if (aolDataItem.aolVersion >= 5) {
            consumption = enginesDataVM?.consumption
                ?.filter((c) => c.engineDataType === EngineDataTypeEnum.MainEngine)
                .reduce((acc, c) => acc + c.consumptionMt, 0);
        } else {
            consumption = consumptionHfo + consumptionMgo;
        }
        const mainEnginePowerLoggedOutput = mainEngineData.powerLoggedOutput ? mainEngineData.powerLoggedOutput : 0;
        const shaftPowerLoggedOutput = shaftGeneratorData.powerLoggedOutput ? shaftGeneratorData.powerLoggedOutput : 0;

        let text = 'SFOC = (HFO Consumption + MGO Consumption) / ';

        if (hasShaftGenerator && hasPowerLogging) {
            text += `(ME Power Logged Output + Shaft Generator Power Logged Output * 1.01)`;
        } else {
            text += `ME Power Logged Output"))`;
        }
        text += ' * 1000000\n';
        enginesDataVM.sfocDetails.push(text);

        if (aolDataItem.aolVersion >= 5) {
            text = `SFOC = (`;
            text += enginesDataVM.consumption
                .filter((c) => c.engineDataType === EngineDataTypeEnum.MainEngine && c.consumptionMt > 0)
                .map((c) => c.consumptionMt?.toFixed(2))
                .join(' + ');
            text += `) / `;
        } else {
            text = `SFOC = (${consumptionHfo.toFixed(2)} + ${consumptionMgo.toFixed(2)}) / `;
        }

        if (hasShaftGenerator && hasPowerLogging) {
            text += `(${mainEngineData.powerLoggedOutput} + ${shaftGeneratorData.powerLoggedOutput} * 1.01)`;
        } else {
            text += `${mainEngineData.powerLoggedOutput}`;
        }
        text += ' * 1000000\n';
        enginesDataVM.sfocDetails.push(text);

        text = `SFOC = ${consumption.toFixed(2)} / `;

        if (hasShaftGenerator && hasPowerLogging) {
            text += `(${mainEnginePowerLoggedOutput + shaftPowerLoggedOutput * 1.01})`;
        } else {
            text += `${mainEngineData.powerLoggedOutput}`;
        }
        text += ' * 1000000\n';
        enginesDataVM.sfocDetails.push(text);

        text = `SFOC = ${getSfoc(mainEngineData, shaftGeneratorData)?.toFixed(2)} g/kWh`;
        enginesDataVM.sfocDetails.push(text);
    };

    const updateHasMissingMeConsumption = (vm: EnginesDataVM) => {
        if (
            (aolDataItem.aolVersion >= 5 &&
                (aolDataItem.aolDataItemProperties.shiftingAndMooringPortActivityHours ?? 0) > 0 &&
                !enginesDataVM.consumption?.some(
                    (consumption) => consumption.engineDataType === EngineDataTypeEnum.MainEngine && consumption.consumptionMt > 0
                )) ||
            (aolDataItem.aolVersion < 5 &&
                (aolDataItem.aolDataItemProperties.shiftingAndMooringPortActivityHours ?? 0) > 0 &&
                vm.mainEngineData.consumptionHfo === 0 &&
                vm.mainEngineData.consumptionMgo === 0)
        ) {
            setHasMissingMeConsumption(true);
        } else {
            setHasMissingMeConsumption(false);
        }
    };

    const resetHasEmptyValues = () => {
        if (hasEmptyValuesLocal) {
            setHasEmptyValuesLocal(false);
            hasEmptyValues.current = false;
        }
    };

    const getMaxPowerLoggedOutput = () => {
        if (isEquippedWithPowerLoggingForMainEngine()) {
            const maxLoad = vessel?.vesselConfigurationV3!.engineConfiguration.mainEngine.loadAt100Percent;
            if (maxLoad !== undefined) {
                return maxLoad * (enginesDataVM?.mainEngineData?.runningHours ?? 0);
            }
        }
    };
    const getMaxPowerOutput = () => {
        const defaultValue = aolConstants.DEFAULT_MAX_POWER_OUTPUT;
        const maxLoad = vessel?.vesselConfigurationV3!.engineConfiguration.mainEngine.loadAt100Percent;
        return maxLoad ?? defaultValue;
    };

    const showSlip = (engineDataVM?: EngineDataVM, observedSpeed?: number) => {
        return (
            vessel?.showSlip &&
            reportType === NoonReportTypeEnum.Sea &&
            (engineDataVM?.pitch !== undefined ? engineDataVM.pitch > 0 : false) &&
            (engineDataVM?.revolutionsPerMinute !== undefined ? engineDataVM.revolutionsPerMinute > 0 : false) &&
            observedSpeed !== undefined &&
            observedSpeed > 0
        );
    };

    const getMaxRpm = () => {
        if (vessel?.vesselConfigurationV3!.engineConfiguration.mainEngine) {
            return vessel?.vesselConfigurationV3.engineConfiguration.mainEngine.rpmAt100Percent;
        }
    };

    const getTheoreticalSpeed = (mainEngineDataVM: EngineDataVM, observedSpeed?: number) => {
        const hasSlip = showSlip(mainEngineDataVM, observedSpeed);

        if (hasSlip && mainEngineDataVM.pitch && mainEngineDataVM.revolutionsPerMinute && observedSpeed) {
            let theoreticalSpeed = (mainEngineDataVM.pitch * mainEngineDataVM.revolutionsPerMinute * 6) / 1852;

            if (vessel?.mainEngineRpmReductionFactor && vessel.mainEngineRpmReductionFactor !== 1) {
                theoreticalSpeed /= vessel.mainEngineRpmReductionFactor;
            }

            return theoreticalSpeed;
        }

        return 0;
    };

    const getSlip = (mainEngineDataVM?: EngineDataVM, observedSpeed?: number) => {
        const hasSlip = showSlip(mainEngineDataVM, observedSpeed);

        if (mainEngineDataVM && hasSlip && mainEngineDataVM.pitch && mainEngineDataVM.revolutionsPerMinute && observedSpeed) {
            const theoreticalSpeed = getTheoreticalSpeed(mainEngineDataVM, observedSpeed);
            return theoreticalSpeed > 0 ? (1 - observedSpeed / theoreticalSpeed) * 100 : undefined;
        }
    };

    const showSfoc = (mainEngineData?: EngineDataVM) => {
        if (!mainEngineData) return false;
        const hasShaftGenerator = vessel?.vesselConfigurationV3!.isEquippedWithShaftGenerator;
        const hasPowerLogging = vessel?.vesselConfigurationV3!.isEquippedWithPowerLoggingForAuxiliaryEngine;
        const hasTotalConsumption =
            aolDataItem.aolVersion >= 5
                ? enginesDataVM.consumption?.some((c) => c.engineDataType === EngineDataTypeEnum.MainEngine && c.consumptionMt > 0)
                : mainEngineData.consumptionHfo! + mainEngineData.consumptionMgo! > 0;

        return (
            vessel?.showMainEngineSfoc &&
            reportType === NoonReportTypeEnum.Sea &&
            isEquippedWithPowerLoggingForMainEngine() &&
            (!hasShaftGenerator || (hasShaftGenerator && hasPowerLogging)) &&
            hasTotalConsumption &&
            mainEngineData.powerLoggedOutput &&
            mainEngineData.powerLoggedOutput > 0
        );
    };

    const getSfoc = (mainEngineData?: EngineDataVM, shaftGeneratorData?: EngineDataVM) => {
        if (!mainEngineData) return;
        const consumptionHfo = mainEngineData.consumptionHfo ? mainEngineData.consumptionHfo : 0;
        const consumptionMgo = mainEngineData.consumptionMgo ? mainEngineData.consumptionMgo : 0;
        let powerLoggedOutput = mainEngineData.powerLoggedOutput ? mainEngineData.powerLoggedOutput : 0;

        let consumption = 0;
        if (aolDataItem.aolVersion >= 5) {
            consumption = enginesDataVM?.consumption
                ?.filter((c) => c.engineDataType === EngineDataTypeEnum.MainEngine)
                .reduce((acc, c) => acc + c.consumptionMt, 0);
        } else {
            consumption = consumptionHfo + consumptionMgo;
        }

        if (showSfoc(mainEngineData)) {
            if (shaftGeneratorData?.powerLoggedOutput) {
                powerLoggedOutput += shaftGeneratorData.powerLoggedOutput * 1.01;
            }

            return powerLoggedOutput !== 0 ? (consumption / powerLoggedOutput) * 1000000 : null;
        }

        return null;
    };

    const isSfocValid = (mainEngineData?: EngineDataVM, shaftGeneratorData?: EngineDataVM) => {
        if (!mainEngineData || !shaftGeneratorData) return false;

        const sfoc = getSfoc(mainEngineData, shaftGeneratorData);
        return sfoc && sfoc > 160 && sfoc < 230;
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.hours;
        resetHasEmptyValues();
        hasInvalidFields(enginesDataVM, changeObject.name, changeObject.isValid);
        validateFieldsOnRunningHoursChange();
        onChange();
        checkRunningHours();
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;

        changeObject.dataObject[name] = value ? +value : undefined;
        const voyageFuelPrice = changeObject.extraArgs?.aolItem?.voyageNumber ? getVoyageFuelPrice(changeObject.extraArgs) : null;
        if (name === 'consumptionHfo') {
            changeObject.dataObject.consumptionHfoCost = getConsumptionCost(FuelType.HFO, voyageFuelPrice, +value);
        } else if (name === 'consumptionMgo') {
            changeObject.dataObject.consumptionMgoCost = getConsumptionCost(FuelType.LGO, voyageFuelPrice, +value);
        } else if (name === 'consumptionMt') {
            if (changeObject.dataObject.consumptionMt === undefined) changeObject.dataObject.consumptionMt = 0;
            changeObject.dataObject.cost = getConsumptionCostByFuelType(changeObject.dataObject as Consumption, voyageFuelPrice);
        }
        resetHasEmptyValues();
        hasInvalidFields(enginesDataVM, name, changeObject.isValid);
        onChange();
        checkRunningHours();
        updateTotalMainEngineConsumption();
        updateSlipDetails();
        updateSfocDetails();
        updateHasMissingMeConsumption(enginesDataVM);
    };

    const validateFieldsOnRunningHoursChange = () => {
        if (isAtSea() && isEquippedWithPowerLoggingForMainEngine()) {
            const powerLoggedOutput = enginesDataVM.mainEngineData?.powerLoggedOutput;
            const maxPowerLoggedOutput = getMaxPowerLoggedOutput();
            const isValid = !isNullOrUndefined(powerLoggedOutput) && powerLoggedOutput <= maxPowerLoggedOutput;
            hasInvalidFields(enginesDataVM, 'powerLoggedOutput', isValid);
        }
    };

    const updateTotalMainEngineConsumption = () => {
        let cons = 0;

        if (aolDataItem.aolVersion >= 5) {
            cons = enginesDataVM?.consumption
                ?.filter((c) => c.engineDataType === EngineDataTypeEnum.MainEngine)
                .reduce((acc, c) => acc + c.consumptionMt, 0);
        } else {
            cons = +(enginesDataVM?.mainEngineData?.consumptionHfo ?? 0) + +(enginesDataVM?.mainEngineData?.consumptionMgo ?? 0);
        }
        setTotalMainEngineConsumption(cons);
    };

    const setTotalMainEngineConsumption = (value: number) => {
        if (!aolDataItem) return;

        aolDataItem.aolDataItemProperties.totalMainEngineConsumption = value;
    };

    const getEngineLoad = (mainEngineDataVM?: EngineDataVM): number | undefined => {
        const maxLoad = vessel?.vesselConfigurationV3!.engineConfiguration.mainEngine.loadAt100Percent;
        const _isEquippedWithPowerLoggingForMainEngine = isEquippedWithPowerLoggingForMainEngine();
        if (
            _isEquippedWithPowerLoggingForMainEngine &&
            mainEngineDataVM !== undefined &&
            mainEngineDataVM.powerLoggedOutput !== undefined &&
            (mainEngineDataVM.runningHours ?? 0) > 0 &&
            (maxLoad ?? 0) > 0
        ) {
            return roundTo0Decimal((mainEngineDataVM.powerLoggedOutput! / maxLoad! / mainEngineDataVM.runningHours!) * 100);
        }
        if (
            !_isEquippedWithPowerLoggingForMainEngine &&
            mainEngineDataVM !== undefined &&
            mainEngineDataVM.powerOutput !== undefined &&
            (mainEngineDataVM.runningHours ?? 0) > 0 &&
            (maxLoad ?? 0) > 0
        ) {
            return roundTo0Decimal((mainEngineDataVM.powerOutput! / maxLoad!) * 100);
        }
    };

    const isAtSea = () => reportType === NoonReportTypeEnum.Sea;

    const isEquippedWithPowerLoggingForMainEngine = () => vessel?.vesselConfigurationV3!.isEquippedWithPowerLoggingForMainEngine;

    const checkRunningHours = () => {
        let hasInconsistency = false;
        if (
            aolDataItem.aolVersion >= 5 &&
            enginesDataVM.mainEngineData.runningHours === 0 &&
            enginesDataVM.consumption?.some(
                (consumption) => consumption.engineDataType === EngineDataTypeEnum.MainEngine && consumption.consumptionMt > 0
            )
        ) {
            hasInconsistency = true;
        } else if (
            aolDataItem.aolVersion < 5 &&
            enginesDataVM.mainEngineData.runningHours === 0 &&
            ((enginesDataVM.mainEngineData?.consumptionHfo && enginesDataVM.mainEngineData?.consumptionHfo > 0) ||
                (enginesDataVM?.mainEngineData?.consumptionMgo && enginesDataVM?.mainEngineData?.consumptionMgo > 0))
        ) {
            hasInconsistency = true;
        }

        setIsRunningHoursInconsistent(hasInconsistency);
        hasEmptyValues.current = hasInconsistency;
    };

    const checkOnSave = () => {
        const ignoreList: string[] = ['consumptionHfoCost', 'consumptionMgoCost'];
        if (!isEquippedWithPowerLoggingForMainEngine()) {
            ignoreList.push('powerLoggedOutput');
        } else {
            ignoreList.push('powerOutput');
        }

        if (!isAtSea()) {
            ignoreList.push('powerLoggedOutput');
            ignoreList.push('powerOutput');
            ignoreList.push('pitch', 'revolutionsPerMinute');
        }
        if (hasMissingValues(enginesDataVM.mainEngineData, ignoreList)) {
            setHasEmptyValuesLocal(true);
            hasEmptyValues.current = true;
        } else {
            if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
            hasEmptyValues.current = false;
        }

        checkRunningHours();
    };

    return (
        <>
            <MainEngineSectionWrapper>
                <SectionInnerWrapper>
                    <SubSectionHeader>Main engine</SubSectionHeader>
                    {isRunningHoursInconsistent && <ErrorText>Running hours cannot be 0</ErrorText>}
                    <OdfjellInputComponent
                        isReadOnly={isReadOnly}
                        type={'time'}
                        description={'Running hours'}
                        name={'runningHours'}
                        max={hoursInReport}
                        dataObject={enginesDataVM.mainEngineData}
                        value={enginesDataVM.mainEngineData?.runningHours}
                        onTimeInputChange={onTimeInputChange}
                        unit={''}
                    />
                    {aolDataItem.aolVersion >= 5 ? (
                        <ConsumptionContainer
                            aolItem={aolDataItem}
                            engineType={EngineDataTypeEnum.MainEngine}
                            consumption={enginesDataVM?.consumption}
                            isReadOnly={isReadOnly}
                            onChange={onInputChange}
                            addConsumptionRow={addConsumptionRow}
                        />
                    ) : (
                        <>
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'number'}
                                value={enginesDataVM.mainEngineData?.consumptionHfo}
                                dataObject={enginesDataVM.mainEngineData}
                                description={'HFO'}
                                onChange={onInputChange}
                                name={'consumptionHfo'}
                                min={0}
                                max={100}
                                unit={'MT'}
                                extraArgs={aolDataItem}
                                cost={enginesDataVM.mainEngineData?.consumptionHfoCost}
                            />
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'number'}
                                value={enginesDataVM.mainEngineData?.consumptionMgo}
                                dataObject={enginesDataVM.mainEngineData}
                                description={'MGO'}
                                onChange={onInputChange}
                                name={'consumptionMgo'}
                                min={0}
                                max={100}
                                unit={'MT'}
                                extraArgs={aolDataItem}
                                cost={enginesDataVM.mainEngineData?.consumptionMgoCost}
                            />
                        </>
                    )}
                    {isAtSea() && (
                        <>
                            {isEquippedWithPowerLoggingForMainEngine() && (
                                <>
                                    <OdfjellInputComponent
                                        isReadOnly={isReadOnly}
                                        type={'number'}
                                        description={'Power logged output'}
                                        value={enginesDataVM.mainEngineData?.powerLoggedOutput}
                                        dataObject={enginesDataVM.mainEngineData}
                                        onChange={onInputChange}
                                        unit={'kWh'}
                                        name={'powerLoggedOutput'}
                                        min={0}
                                        max={getMaxPowerLoggedOutput()}
                                        extraArgs={aolDataItem}
                                    />
                                </>
                            )}
                            {!isEquippedWithPowerLoggingForMainEngine() && (
                                <>
                                    <OdfjellInputComponent
                                        isReadOnly={isReadOnly}
                                        type={'number'}
                                        description={'Power output'}
                                        value={enginesDataVM.mainEngineData?.powerOutput}
                                        dataObject={enginesDataVM.mainEngineData}
                                        onChange={onInputChange}
                                        unit={'kW'}
                                        name={'powerOutput'}
                                        min={0}
                                        max={getMaxPowerOutput()}
                                        extraArgs={aolDataItem}
                                        tooltipId={'mainEngineData.powerOutput'}
                                    />
                                </>
                            )}
                            <OdfjellInputComponent
                                isReadOnly={true}
                                type={'number'}
                                description={'Engine load'}
                                value={getEngineLoad(enginesDataVM.mainEngineData)}
                                dataObject={enginesDataVM.mainEngineData}
                                unit={'%'}
                                name={'engineLoad'}
                            />
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'number'}
                                description={'Pitch'}
                                value={enginesDataVM.mainEngineData?.pitch}
                                onChange={onInputChange}
                                dataObject={enginesDataVM.mainEngineData}
                                unit={'dm'}
                                min={0}
                                max={65}
                                name={'pitch'}
                                extraArgs={aolDataItem}
                            />
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'number'}
                                description={'RPM'}
                                value={enginesDataVM.mainEngineData?.revolutionsPerMinute}
                                onChange={onInputChange}
                                dataObject={enginesDataVM.mainEngineData}
                                name={'revolutionsPerMinute'}
                                min={0}
                                max={getMaxRpm()}
                                unit={'rpm'}
                                extraArgs={aolDataItem}
                            />
                            {showSlip(enginesDataVM.mainEngineData, enginesDataVM.observedSpeed) && (
                                <div className="input-and-icon">
                                    <OdfjellInputComponent
                                        type={'number'}
                                        isReadOnly={true}
                                        description={'Slip'}
                                        value={getSlip(enginesDataVM.mainEngineData, enginesDataVM.observedSpeed)?.toFixed(2)}
                                        unit={'%'}
                                    />
                                    <div
                                        className="slip-details-button-wrapper"
                                        onMouseEnter={() => setSlipDetailsExpanded(true)}
                                        onMouseLeave={() => setSlipDetailsExpanded(false)}
                                    >
                                        <button className="slip-details-button" aria-label="Details...">
                                            <FontAwesomeIcon icon="info-circle" />
                                        </button>
                                    </div>
                                    <span
                                        className={'slip-details ' + (slipDetailsExpanded ? 'slip-details-expanded' : 'slip-details-hidden')}
                                        key={`${aolDataItem.id}_slip_details`}
                                    >
                                        {enginesDataVM?.slipDetails?.map((o, i) => (
                                            <span className="slip-detail" key={`${i}_slip_detail`}>
                                                {o}
                                            </span>
                                        ))}
                                    </span>
                                </div>
                            )}
                            {showSfoc(enginesDataVM.mainEngineData) === true && (
                                <div className="input-and-icon">
                                    <OdfjellInputComponent
                                        type={'number'}
                                        isReadOnly={true}
                                        description={'SFOC'}
                                        value={getSfoc(enginesDataVM.mainEngineData, enginesDataVM.shaftGeneratorData)?.toFixed(2)}
                                        unit={'g/kWh'}
                                    />
                                    <div
                                        className="sfoc-details-button-wrapper"
                                        onMouseEnter={() => setSfocDetailsExpanded(true)}
                                        onMouseLeave={() => setSfocDetailsExpanded(false)}
                                    >
                                        <button className="sfoc-details-button" aria-label="Details...">
                                            <FontAwesomeIcon icon="info-circle" />
                                        </button>
                                    </div>
                                    <span
                                        className={'sfoc-details ' + (sfocDetailsExpanded ? 'sfoc-details-expanded' : 'sfoc-details-hidden')}
                                        key={`${aolDataItem.id}_sfoc_details`}
                                    >
                                        {enginesDataVM?.sfocDetails?.map((o, i) => (
                                            <span className="sfoc-detail" key={`${i}_sfoc_detail`}>
                                                {o}
                                            </span>
                                        ))}
                                    </span>
                                </div>
                            )}
                            {showSfoc(enginesDataVM.mainEngineData) === true &&
                                !isSfocValid(enginesDataVM.mainEngineData, enginesDataVM.shaftGeneratorData) && (
                                    <ErrorMessageWrapper>
                                        The SFOC is outside the expected range [160, 230]. Please double check and verify the AOL inputs.
                                    </ErrorMessageWrapper>
                                )}
                        </>
                    )}
                </SectionInnerWrapper>
                {hasMissingMeConsumption && <WarningText>Missing ME consumption during river or port maneuvering.</WarningText>}

                {hasEmptyValuesLocal && <ErrorText>Missing main engine.</ErrorText>}
            </MainEngineSectionWrapper>
        </>
    );
};

export default MainEngineSectionComponent;
